import { notyf } from '@modules';
import { pubsub } from '@services';

class CartItems extends HTMLElement {
  wishlistButton = this.querySelector('[data-swish-move-to-wishlist]');
  wishlistMoveJustHappened = false;
  handleMoveToWishlist = this.wishlistButton ? (event) => this.confirmMoveToWishlist(event) : null;

  connectedCallback() {
    this.unsubscribeCartChange = pubsub.subscribe('cart:change', ({ line, quantity }) =>
      this.cartChange(line, quantity)
    );
    this.unsubscribeRefreshCart = pubsub.subscribe('cart:added', () => this.refreshCart());

    if (this.handleMoveToWishlist) {
      document.addEventListener('wk:wishlist:save-for-later:success', this.handleMoveToWishlist);
    }
  }

  disconnectedCallback() {
    this.unsubscribeCartChange();
    this.unsubscribeRefreshCart();

    if (this.handleMoveToWishlist) {
      document.removeEventListener('wk:wishlist:save-for-later:success', this.handleMoveToWishlist);
    }
  }

  /**
   * Change the cart.
   * @description Use the /{locale}/cart/change.js endpoint to change the quantity, properties, and selling_plan properties of a cart line item. Only items already in your cart can be changed, and you can change only one line item at a time.
   * @tutorial https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async cartChange(line, quantity) {
    pubsub.publish('cart:updating', {
      line,
    });
    const body = JSON.stringify({
      line,
      quantity,
      sections: 'fetch-line-items',
      sections_url: window.location.pathname,
    });

    try {
      const { error, data } = await this.fetchCart(routes.cart_change_url, body);

      if (error) throw new Error(error);

      this.renderSections(data.sections);

      pubsub.publish('cart:updated', {
        itemCount: data.item_count,
      });

      if (!this.wishlistMoveJustHappened) notyf.success(window.toast_translations.cart.updated);
      this.wishlistMoveJustHappened = false;
    } catch (error) {
      console.error(error);
      notyf.error(error.message);

      pubsub.publish('cart:error', {
        line,
      });
    }
  }

  /**
   * Fetch the cart.
   * @param {string} input - The URL to fetch.
   * @param {string} body - The request body.
   * @returns {Promise} The response.
   *
   * @example
   * const json = await this.fetchCart(routes.cart_change_url, body);
   */
  async fetchCart(input, body) {
    const response = await fetch(input, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    });

    const data = await response.json();

    if (!response.ok) return { error: data.errors, data: null };

    return { error: null, data };
  }

  /**
   * Refresh the cart.
   */
  async refreshCart() {
    try {
      const response = await fetch(`${Shopify.routes.root}?sections=fetch-line-items`);
      const data = await response.json();

      const sections = {};
      sections['fetch-line-items'] = data['fetch-line-items'];

      pubsub.publish('cart:updated', {
        itemCount: data.item_count,
      });

      this.renderSections(sections);
    } catch (error) {
      notyf.error(error.message);
    }
  }

  /**
   * Shows confirmation of moving the current item to the wishlist.
   * @param {CustomEvent} event - The Swish app Wishlist event containing the confirmation text.
   */
  confirmMoveToWishlist(event) {
    this.wishlistMoveJustHappened = true;
    notyf.success(event.detail.confirmationText);
  }

  /**
   * Render the sections.
   * @param {Object} json - The JSON response.
   * @example
   * const json = await this.fetchCart(routes.cart_change_url, body);
   * this.renderSections(json);
   */
  renderSections(sections) {
    const html = new DOMParser().parseFromString(sections['fetch-line-items'], 'text/html');
    this.innerHTML = html.querySelector('line-items').innerHTML;
  }
}

customElements.define('line-items', CartItems);
